/**
 * Login form component for authentication.
 */

import PrivacyPolicyModal from '@/components/modals/PrivacyPolicyModal';
import AboutModal from '@/components/modals/AboutModal';
import { ApiDocsLinks } from '@/components/common/ApiDocsLinks';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/apiClient';
import { BACKEND_URL, API_ENDPOINTS } from '@/config/settings';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Navbar, Row, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import LoginLanguageSelector from './LoginLanguageSelector';

interface LoginProps {
  isProcessingAuth?: boolean;
}

export default function Login({ isProcessingAuth = false }: LoginProps) {
  const { login, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [loginMessage, setLoginMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);

  // Check if we're processing OAuth callback
  const oauthCode = searchParams.get('oauth_code');
  const verifyToken = searchParams.get('verify');
  const isProcessingOAuth = isProcessingAuth || !!oauthCode || !!verifyToken;

  // Fetch CAPTCHA
  const { data: captchaData, refetch: refetchCaptcha } = useQuery({
    queryKey: ['captcha'],
    queryFn: async () => {
      const response = await apiClient.getCaptcha();
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as { image: string; captcha_id: string };
    },
    enabled: showEmailLogin,
  });

  const handleGoogleLogin = () => {
    // Directly redirect to Google OAuth endpoint
    // The backend will handle the redirect to Google or return an error
    // Using window.location instead of fetch to avoid CORS issues with redirects
    // Use configured backend URL for environment-independent OAuth flow
    const oauthUrl = `${BACKEND_URL}${API_ENDPOINTS.google_oauth_login}`;
    window.location.href = oauthUrl;
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginMessage(null);

    // Check if locked out due to too many failed attempts
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingSeconds = Math.ceil((lockoutUntil - Date.now()) / 1000);
      const lockoutMessage = `Too many login attempts. Please wait ${remainingSeconds} seconds.`;
      setLoginMessage({ type: 'danger', text: lockoutMessage });
      toast.error(lockoutMessage);
      return;
    }

    if (!captchaData) {
      toast.error(t('login.waitForCaptcha'));
      return;
    }

    try {
      const response = await apiClient.authenticateUser(
        email,
        captchaCode,
        captchaData.captcha_id
      );

      if (response.error) {
        // Increment failed attempts
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);

        // Lockout after 3 failed attempts
        if (newAttempts >= 3) {
          const lockoutDuration = 60000; // 1 minute
          setLockoutUntil(Date.now() + lockoutDuration);
          const lockoutMsg = 'Too many failed attempts. Please wait 1 minute before trying again.';
          setLoginMessage({ type: 'danger', text: lockoutMsg });
          toast.error(lockoutMsg);
        } else {
          const attemptsRemaining = 3 - newAttempts;
          setLoginMessage({
            type: 'danger',
            text: `${response.error} (${attemptsRemaining} attempt${attemptsRemaining === 1 ? '' : 's'} remaining)`
          });
        }

        refetchCaptcha();
        setCaptchaCode('');
        return;
      }

      // Reset rate limiting on successful login
      setLoginAttempts(0);
      setLockoutUntil(null);

      // Backend sets httponly cookie via Set-Cookie header, just update auth state
      login();
      setLoginMessage({ type: 'success', text: t('login.loginLinkSent') });
      toast.success(t('login.loginLinkSent'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('login.loginFailed');
      setLoginMessage({ type: 'danger', text: errorMessage });
      refetchCaptcha();
    }
  };

  // Redirect to homepage if already authenticated
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  // Show loading state when processing OAuth or magic link
  if (isProcessingOAuth) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', marginBottom: '1rem' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p style={{ color: 'var(--myant-text-secondary)', fontSize: '1rem' }}>
          {t('login.completingSignIn', 'Completing sign in...')}
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top toolbar for navigation */}
      <Navbar expand="lg" style={{ backgroundColor: 'var(--myant-bg-primary)', borderBottom: '1px solid var(--myant-border-color)' }}>
        <Container>
          <Navbar.Brand
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/home')}
          >
            <img
              src="/myant-logo.png"
              alt="Myant Logo"
              style={{
                height: '32px',
                width: 'auto',
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <span style={{ fontWeight: 600, fontSize: '1.25rem', color: 'var(--myant-text-primary)' }}>
              {t('navbar.dataShop')}
            </span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="login-navbar-nav" />
          <Navbar.Collapse id="login-navbar-nav" className="justify-content-end">
            <LoginLanguageSelector />
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <Container className="py-5" style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Row className="justify-content-center w-100">
          <Col md={6} lg={5}>
          <div className="text-center mb-4">
            <div style={{ marginBottom: '1.5rem' }}>
              <img
                src="/myant-logo.png"
                alt="Myant Logo"
                style={{
                  height: '48px',
                  width: 'auto',
                  marginBottom: '1rem',
                }}
                onError={(e) => {
                  // Fallback: hide image if it fails to load
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            <h1 style={{ fontWeight: 600, fontSize: '2rem', marginBottom: '0.5rem', color: 'var(--myant-text-primary)' }}>
              {t('login.title')}
            </h1>
            <p style={{ color: 'var(--myant-text-secondary)', fontSize: '1rem', marginBottom: '0.25rem' }}>
              {t('login.subtitle')}
            </p>
            <p style={{ color: 'var(--myant-text-secondary)', fontSize: '0.875rem', fontStyle: 'italic' }}>
              {t('login.product')}
            </p>
          </div>
          <Card style={{ border: 'none', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}>
            <Card.Body className="p-4">
              {/* Google login button */}
              <Button
                variant="primary"
                className="w-100 mb-3"
                onClick={handleGoogleLogin}
                style={{
                  height: '48px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fab fa-google me-2"></i>
                {t('login.signInWithGoogle')}
              </Button>
              <div className="text-center mb-3" style={{ position: 'relative' }}>
                <hr style={{ margin: '1.5rem 0', borderColor: 'var(--myant-border-color)' }} />
                <span
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'var(--myant-bg-primary)',
                    padding: '0 1rem',
                    color: 'var(--myant-text-secondary)',
                    fontSize: '0.875rem'
                  }}
                  >
                  {t('common.or')}
                </span>
              </div>
              <details>
                <summary
                  style={{
                    cursor: 'pointer',
                    color: 'var(--myant-text-secondary)',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    textAlign: 'center',
                    padding: '0.5rem 0'
                  }}
                  onClick={() => setShowEmailLogin(!showEmailLogin)}
                >
                  {t('login.emailLogin')}
                </summary>
                {showEmailLogin && (
                  <div className="mt-4">
                    <p className="text-center mb-3" style={{ color: 'var(--myant-text-secondary)' }}>
                      {t('login.enterEmail')}
                    </p>
                    <Form onSubmit={handleEmailLogin}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="email"
                          placeholder={t('login.emailPlaceholder')}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          style={{ height: '48px' }}
                        />
                      </Form.Group>
                      {captchaData && (
                        <div className="mb-3 text-center">
                          <img
                            src={captchaData.image}
                            alt="CAPTCHA"
                            className="img-fluid mb-2"
                            style={{ border: '1px solid var(--myant-border-color)', borderRadius: 'var(--myant-radius-md)' }}
                          />
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 mb-2"
                            onClick={() => refetchCaptcha()}
                            style={{ color: 'var(--myant-primary)', textDecoration: 'none' }}
                          >
                            {t('login.refreshCaptcha')}
                          </Button>
                        </div>
                      )}
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          placeholder={t('login.captchaPlaceholder')}
                          value={captchaCode}
                          onChange={(e) => setCaptchaCode(e.target.value)}
                          required
                          style={{ height: '48px' }}
                        />
                      </Form.Group>
                      <Button type="submit" variant="primary" className="w-100" style={{ height: '48px', fontSize: '1rem', fontWeight: 500 }}>
                        {t('login.sendLoginLink')}
                      </Button>
                    </Form>
                  </div>
                )}
              </details>
              {loginMessage && (
                <Alert variant={loginMessage.type} className="mt-3">
                  {loginMessage.text}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    <footer
      style={{
        padding: '1.5rem 0',
        borderTop: '1px solid var(--myant-border-color)',
        textAlign: 'center',
        backgroundColor: 'var(--myant-bg-primary)',
      }}
    >
      <div
        style={{
          color: 'var(--myant-text-secondary)',
          fontSize: '0.875rem',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <span>© {new Date().getFullYear()} Myant Inc. All rights reserved.</span>
        <span>|</span>
        <button
          onClick={() => setShowPrivacyModal(true)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--myant-primary)',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.875rem',
            padding: 0,
            margin: '0 0.25rem',
          }}
        >
          {t('footer.privacyPolicy')}
        </button>
        <span>|</span>
        <button
          onClick={() => setShowAboutModal(true)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--myant-primary)',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.875rem',
            padding: 0,
            margin: '0 0.25rem',
          }}
        >
          {t('footer.about')}
        </button>
        <ApiDocsLinks />
      </div>
    </footer>
    <PrivacyPolicyModal
      show={showPrivacyModal}
      onHide={() => setShowPrivacyModal(false)}
    />
    <AboutModal
      show={showAboutModal}
      onHide={() => setShowAboutModal(false)}
    />
    </div>
  );
}
